import type { RecommendationLabInputOptions, RecommendationLabSurveyQuestion } from '../../models/RecommendationLab';
import type { LabPreset } from './data';

export type RecommendationLabSurveyAnswers = Record<string, string[]>;

export function resolveSurveyOptions(
  inputOptions: RecommendationLabInputOptions,
  question: RecommendationLabSurveyQuestion,
): RecommendationLabSurveyQuestion['options'] {
  if (question.options.length > 0 || !question.optionsFromQuestionId) {
    return question.options;
  }
  return (
    inputOptions.survey.questions.find((candidate) => candidate.questionId === question.optionsFromQuestionId)
      ?.options ?? []
  );
}

export function availableSurveyOptionValues(
  inputOptions: RecommendationLabInputOptions,
  question: RecommendationLabSurveyQuestion,
  answers: RecommendationLabSurveyAnswers,
): Set<string> {
  const configuredValues = new Set(resolveSurveyOptions(inputOptions, question).map((option) => option.value));
  if (!question.optionsFromQuestionId) {
    return configuredValues;
  }
  return new Set((answers[question.optionsFromQuestionId] ?? []).filter((value) => configuredValues.has(value)));
}

export function buildPresetSurveyAnswers(
  preset: LabPreset,
  inputOptions: RecommendationLabInputOptions,
): RecommendationLabSurveyAnswers {
  const answers: RecommendationLabSurveyAnswers = {};
  for (const selection of preset.surveySelections) {
    const question = inputOptions.survey.questions.find((candidate) => candidate.questionId === selection.questionId);
    if (!question) {
      continue;
    }
    const availableValues = availableSurveyOptionValues(inputOptions, question, answers);
    answers[question.questionId] = selection.values.filter((value) => availableValues.has(value));
  }
  return normalizeSurveyAnswers(inputOptions, answers);
}

export function normalizeSurveyAnswers(
  inputOptions: RecommendationLabInputOptions,
  answers: RecommendationLabSurveyAnswers,
): RecommendationLabSurveyAnswers {
  return inputOptions.survey.questions.reduce<RecommendationLabSurveyAnswers>((normalized, question) => {
    const availableValues = availableSurveyOptionValues(inputOptions, question, normalized);
    let selected = [...new Set((answers[question.questionId] ?? []).filter((value) => availableValues.has(value)))];
    const exclusiveValue = selected.find((value) =>
      question.options.some((option) => option.value === value && option.clearsOtherOptions),
    );
    if (exclusiveValue) {
      selected = [exclusiveValue];
    } else if (question.type === 'single') {
      selected = selected.slice(0, 1);
    } else if (question.maxSelections !== undefined) {
      selected = selected.slice(0, question.maxSelections);
    }
    if (selected.length > 0) {
      normalized[question.questionId] = selected;
    }
    return normalized;
  }, {});
}

export function toggleSurveyAnswer(
  question: RecommendationLabSurveyQuestion,
  current: string[],
  optionValue: string,
): string[] {
  if (question.type === 'single') {
    return [optionValue];
  }

  const option = question.options.find((candidate) => candidate.value === optionValue);
  if (!option) {
    return current;
  }
  if (current.includes(optionValue)) {
    return current.filter((value) => value !== optionValue);
  }
  if (option.clearsOtherOptions) {
    return [optionValue];
  }

  const exclusiveValues = new Set(
    question.options.filter((candidate) => candidate.clearsOtherOptions).map((candidate) => candidate.value),
  );
  const withoutExclusive = current.filter((value) => !exclusiveValues.has(value));
  const maximum = question.maxSelections ?? Number.POSITIVE_INFINITY;
  return withoutExclusive.length >= maximum ? withoutExclusive : [...withoutExclusive, optionValue];
}

export function isSurveyComplete(
  inputOptions: RecommendationLabInputOptions,
  answers: RecommendationLabSurveyAnswers,
): boolean {
  return inputOptions.survey.questions.every((question) => {
    const selected = answers[question.questionId] ?? [];
    const availableValues = availableSurveyOptionValues(inputOptions, question, answers);
    const minimum = question.minSelections ?? (question.required ? 1 : 0);
    const maximum = question.maxSelections ?? (question.type === 'single' ? 1 : Number.POSITIVE_INFINITY);
    const hasExclusiveConflict =
      selected.some((value) =>
        question.options.some((option) => option.value === value && option.clearsOtherOptions),
      ) && selected.length > 1;
    return (
      selected.length >= minimum &&
      selected.length <= maximum &&
      new Set(selected).size === selected.length &&
      selected.every((value) => availableValues.has(value)) &&
      !hasExclusiveConflict
    );
  });
}

export function deriveSurveySkinType(
  inputOptions: RecommendationLabInputOptions,
  answers: RecommendationLabSurveyAnswers,
  fallback: string,
): string {
  const skinTypes = new Set(['combination', 'dry', 'oily', 'normal']);
  const question = inputOptions.survey.questions.find((candidate) => candidate.questionId === 'skintype');
  const selected = question && answers[question.questionId]?.find((value) => skinTypes.has(value));
  if (selected) {
    return selected;
  }
  return fallback;
}
